import React, { useState, useEffect } from "react";
import { ordersAPI, salespersonsAPI, customersAPI } from "../services/api";
import SearchableSelect from "./SearchableSelect";

const getLocalDateString = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const STATUS_OPTIONS = [
  { value: "Ordered",     label: "Ordered" },
  { value: "Not Ordered", label: "Not Ordered" },
];

const OrderModal = ({ order, customerId, onClose }) => {
  const [formData, setFormData] = useState({
    order_date:     getLocalDateString(),
    order_status:   "Ordered",
    order_amount:   "",
    salesperson_id: "",
    notes:          "",
  });
  const [selectedCustomerId, setSelectedCustomerId] = useState(customerId || "");
  const [customers,    setCustomers]    = useState([]);
  const [salespersons, setSalespersons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  useEffect(() => {
    fetchSalespersons();
    if (!customerId) fetchCustomers();
    if (order) {
      setFormData({
        order_date:     order.order_date     || getLocalDateString(),
        order_status:   order.order_status   || "Ordered",
        order_amount:   order.order_amount   || "",
        salesperson_id: order.salesperson_id || "",
        notes:          order.notes          || "",
      });
      if (order.customer_id) setSelectedCustomerId(String(order.customer_id));
    }
  }, [order, customerId]);

  const fetchSalespersons = async () => {
    try {
      const res = await salespersonsAPI.getAll();
      setSalespersons(res.data);
    } catch (err) {
      console.error("Failed to load salespersons:", err);
    }
  };

  const fetchCustomers = async () => {
    try {
      const res = await customersAPI.getAll();
      setCustomers(res.data);
    } catch (err) {
      console.error("Failed to load customers:", err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (!formData.salesperson_id) {
      setError("Please select a salesperson");
      setLoading(false);
      return;
    }

    const effectiveCustomerId = customerId || selectedCustomerId;
    if (!effectiveCustomerId) {
      setError("Please select a customer");
      setLoading(false);
      return;
    }

    try {
      const data = {
        ...formData,
        customer_id:  effectiveCustomerId,
        order_amount: parseFloat(formData.order_amount) || 0,
      };
      if (order) {
        await ordersAPI.update(order.id, data);
      } else {
        await ordersAPI.create(data);
      }
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to save order");
    } finally {
      setLoading(false);
    }
  };

  const setField = (name) => (value) =>
    setFormData(prev => ({ ...prev, [name]: value }));

  const salespersonOptions = salespersons.map(s => ({
    value: String(s.id),
    label: s.name + (s.employee_id ? ` (${s.employee_id})` : ""),
  }));

  const customerOptions = customers.map(c => ({
    value: String(c.id),
    label: c.customer_name + (c.contact_number ? ` — ${c.contact_number}` : ""),
  }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-gray-900 bg-opacity-50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
            <h3 className="text-lg font-semibold text-gray-900">
              {order ? "Edit Order" : "Add New Order"}
            </h3>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="px-5 py-5 space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Customer selector — only when not opened from a customer page */}
            {!customerId && !order && (
              <div>
                <label className="form-label">Customer *</label>
                <SearchableSelect
                  value={selectedCustomerId}
                  onChange={setSelectedCustomerId}
                  options={customerOptions}
                  placeholder="Please select customer"
                  disabled={loading}
                />
              </div>
            )}

            <div>
              <label htmlFor="order_date" className="form-label">Order Date *</label>
              <input
                type="date"
                id="order_date"
                name="order_date"
                required
                className="form-input"
                value={formData.order_date}
                onChange={e => setField("order_date")(e.target.value)}
                disabled={loading}
              />
            </div>

            <div>
              <label className="form-label">Order Status *</label>
              <SearchableSelect
                value={formData.order_status}
                onChange={setField("order_status")}
                options={STATUS_OPTIONS}
                placeholder="Please select status"
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="order_amount" className="form-label">Order Amount (KSH)</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 text-sm">KSH</span>
                </div>
                <input
                  type="number"
                  id="order_amount"
                  name="order_amount"
                  step="0.01"
                  min="0"
                  className="form-input pl-12"
                  placeholder="0.00"
                  value={formData.order_amount}
                  onChange={e => setField("order_amount")(e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <label className="form-label">Salesperson *</label>
              <SearchableSelect
                value={String(formData.salesperson_id)}
                onChange={setField("salesperson_id")}
                options={salespersonOptions}
                placeholder="Please select salesperson"
                disabled={loading}
              />
              <p className="mt-1 text-xs text-gray-500">
                Required — select the salesperson handling this order
              </p>
            </div>

            <div>
              <label htmlFor="notes" className="form-label">Notes</label>
              <textarea
                id="notes"
                name="notes"
                rows={3}
                className="form-textarea"
                placeholder="Add notes about this order..."
                value={formData.notes}
                onChange={e => setField("notes")(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="px-5 py-4 bg-gray-50 rounded-b-xl flex flex-col sm:flex-row-reverse gap-3">
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary w-full sm:w-auto"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="loading-spinner" /> Saving...
                </span>
              ) : order ? "Update Order" : "Add Order"}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="btn btn-secondary w-full sm:w-auto"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OrderModal;
